/**
 * Idempotent runtime guard against `NotFoundError` crashes caused by EXTERNAL
 * DOM mutation — overwhelmingly a browser translation tool (Google Translate,
 * DeepL, Chrome's built-in translate) rewriting the page's text nodes.
 *
 * When an extension replaces a text node, React still holds a reference to the
 * original, so its next commit calls `removeChild` / `insertBefore` against a
 * node the extension already moved. The browser throws NotFoundError from deep
 * in react-dom and the WHOLE shell white-screens — for the subset of users who
 * run such a tool. We cannot stop an extension mutating the page, and we cannot
 * make React "the only owner" of nodes a third party edits, so we harden the
 * two DOM operations instead: if the target is no longer where React expects,
 * no-op (or append) rather than throw. React re-renders from its own virtual
 * tree on the next commit, so the visible result self-corrects.
 *
 * This is the well-established mitigation for facebook/react#11538. It patches
 * `Node.prototype` once, guarded so double-invocation and SSR are safe. The
 * companion `dom-mutation-noise.ts` drops the residual Sentry reports.
 */

let installed = false;

export function installDomMutationGuard(): void {
  if (installed) return;
  if (typeof window === "undefined") return;
  if (typeof Node === "undefined" || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    // The node was already detached/moved by an external mutation — React's
    // reference is stale. Return it instead of throwing NotFoundError.
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    // The reference node is no longer a child of `this` (external mutation
    // moved it). Append `node` to the end so it still lands in the tree,
    // rather than throwing NotFoundError.
    if (child && child.parentNode !== this) {
      return originalInsertBefore.call(this, node, null) as T;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };
}
